import * as React from 'react';
import { cn } from '../utils/cn';

export interface SegmentedControlProps {
  /** Segment labels */
  values: string[];
  /** Currently selected index */
  selectedIndex?: number;
  /** Default selected index */
  defaultSelectedIndex?: number;
  /** Callback when value changes */
  onValueChange?: (value: string, index: number) => void;
  /** Callback when index changes (antd-mobile compat) */
  onChange?: (index: number) => void;
  /** Disable all segments */
  disabled?: boolean;
  /** Additional class names */
  className?: string;
  /** Inline style */
  style?: React.CSSProperties;
}

export function SegmentedControl({
  values,
  selectedIndex: controlledIndex,
  defaultSelectedIndex = 0,
  onValueChange,
  onChange,
  disabled = false,
  className,
  style,
}: SegmentedControlProps) {
  const [internalIndex, setInternalIndex] = React.useState(defaultSelectedIndex);
  const activeIndex = controlledIndex !== undefined ? controlledIndex : internalIndex;

  const handleSelect = (index: number) => {
    if (disabled) return;
    setInternalIndex(index);
    onValueChange?.(values[index], index);
    onChange?.(index);
  };

  return (
    <div
      className={cn(
        'inline-flex border border-accent overflow-hidden',
        disabled && 'opacity-40 pointer-events-none',
        className
      )}
      style={style}
      role="tablist"
    >
      {values.map((value, index) => {
        const isActive = index === activeIndex;
        return (
          <button
            key={index}
            role="tab"
            aria-selected={isActive}
            onClick={() => handleSelect(index)}
            className={cn(
              'px-4 py-2 text-xs font-body font-medium',
              '',
              'transition-all duration-fast ease-smooth',
              'border-r border-accent last:border-r-0',
              '!rounded-none',
              isActive
                ? 'bg-accent text-white'
                : 'bg-transparent text-accent hover:bg-accent/10'
            )}
          >
            {value}
          </button>
        );
      })}
    </div>
  );
}
